import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from 'react'

export type Language = 'en' | 'es' | 'ru' | 'hy' | 'fr' | 'de' | 'it'

const LANGUAGES: Language[] = ['en', 'es', 'ru', 'hy', 'fr', 'de', 'it']

interface LanguageContextProps {
    language: Language
    setLanguage: (language: Language) => void
}

const LanguageContext = createContext<LanguageContextProps | undefined>(
    undefined
)

const LANGUAGE_STORAGE_KEY = 'selected-language'
// Язык в адресе (?lang=ru) — у каждой языковой версии страницы свой URL
// для поисковиков (hreflang, sitemap.xml, готовый HTML от prerender).
const LANGUAGE_PARAM = 'lang'

const isLanguage = (value: unknown): value is Language =>
    LANGUAGES.includes(value as Language)

/** Язык из адреса, иначе сохранённый, иначе английский — сразу, без мигания. */
const initialLanguage = (): Language => {
    try {
        const fromUrl = new URLSearchParams(window.location.search).get(
            LANGUAGE_PARAM
        )
        if (isLanguage(fromUrl)) {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, fromUrl)
            return fromUrl
        }
        const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY)
        if (isLanguage(saved)) return saved
    } catch {
        // Хранилище недоступно (приватный режим) — язык по умолчанию.
    }
    return 'en'
}

export const useLanguage = () => {
    const context = useContext(LanguageContext)
    if (!context) {
        throw new Error('useLanguage must be used within a LanguageProvider')
    }
    return context
}

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [language, setLanguageState] = useState<Language>(initialLanguage)

    useEffect(() => {
        document.documentElement.lang = language
    }, [language])

    // Set language function that saves to localStorage
    const setLanguage = (language: Language) => {
        setLanguageState(language)
        try {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
        } catch {
            // см. initialLanguage
        }
        // Если язык был в адресе — меняем и его, иначе после перезагрузки
        // адрес вернул бы прежний язык.
        const url = new URL(window.location.href)
        if (url.searchParams.has(LANGUAGE_PARAM)) {
            url.searchParams.set(LANGUAGE_PARAM, language)
            window.history.replaceState(window.history.state, '', url.toString())
        }
    }

    return (
        <LanguageContext.Provider value={{ language, setLanguage }}>
            {children}
        </LanguageContext.Provider>
    )
}
